import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import * as Sentry from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';
import { createHead } from '@unhead/vue/client';
import { createPinia } from 'pinia';
import PortalVue from 'portal-vue';
import { createApp as createVueApp, watchEffect } from 'vue';
import { GlobalEvents } from 'vue-global-events';
import { Router } from 'vue-router';

import { registerStickyDirective, settleSticky } from './behaviours/stickyObservers.ts';
import HeightTransition from './components/HeightTransition.vue';
import WidthTransition from './components/WidthTransition.vue';
import { useGlobalStore } from './stores/global.ts';

const head = createHead();
const pinia = createPinia();

// Little pinia plugin to allow stores to "initialize"
declare module 'pinia' {
    export interface DefineStoreOptionsBase<S, Store> {
        created?: (this: Store) => void;
    }
}

pinia.use(({ options, store }) => options.created?.call(store));

export let router: Router;

const isWebKit = /AppleWebKit/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

export function useRouter(instance: Router) {
    router = instance;

    // The router restores scroll positions, so it turns the browser's own
    // restoration off. In WebKit, though, that blanks a back swipe's snapshot of
    // a page left at a different position. So there it stays on, and only turns
    // off while going back or forward. That works as WebKit restores after
    // popstate. Chromium restores before, so would scroll the page being left.
    if (isWebKit) {
        window.history.scrollRestoration = 'auto';
        window.addEventListener('popstate', () => {
            window.history.scrollRestoration = 'manual';
            setTimeout(() => (window.history.scrollRestoration = 'auto'));
        });
    }
}

export function createApp(...args: Parameters<typeof createVueApp>) {
    const app = createVueApp(...args);

    app.use(head);
    app.use(pinia);
    app.use(router);
    app.use(PortalVue);
    app.use(autoAnimatePlugin);

    // TODO: remove global components
    app.component('GlobalEvents', GlobalEvents);
    app.component('HeightTransition', HeightTransition);
    app.component('WidthTransition', WidthTransition);

    registerStickyDirective(app);

    // A navigation's scroll can change which sticky bars are stuck. Update them
    // before the next paint, or they look wrong for a frame.
    router.afterEach(settleSticky);

    // Mark the page, or any element, is-scrolled while it's scrolled down, so
    // the header and dialog headers show their divider. Scroll events don't
    // bubble, so they're caught in the capture phase.
    document.addEventListener(
        'scroll',
        ({ target }) => {
            const el = target === document ? document.documentElement : (target as Element);
            el.classList.toggle('is-scrolled', el.scrollTop > 0);
        },
        { capture: true, passive: true },
    );

    app.directive('scroll-observer', {
        mounted(el) {
            function onScroll() {
                el.classList.toggle('is-scrolled-down', el.scrollTop > 0);
                el.classList.toggle('is-scrolled-right', el.scrollLeft > 0);
                el.classList.toggle(
                    'is-scrolled-up',
                    Math.ceil(el.scrollTop) < el.scrollHeight - el.offsetHeight,
                );
                el.classList.toggle(
                    'is-scrolled-left',
                    Math.ceil(el.scrollLeft) < el.scrollWidth - el.offsetWidth,
                );
            }

            el.addEventListener('scroll', onScroll, { passive: true });

            // Content can overflow without the element resizing, such as when
            // a list loads more items or a child grows, so children's sizes
            // and changes to the children themselves also re-measure.
            const resizeObserver = new ResizeObserver(() => onScroll());
            function observeSizes() {
                resizeObserver.observe(el);
                for (const child of el.children) resizeObserver.observe(child);
            }
            observeSizes();
            const mutationObserver = new MutationObserver(() => {
                observeSizes();
                onScroll();
            });
            mutationObserver.observe(el, { childList: true });

            el._scrollObserverCleanup = () => {
                resizeObserver.disconnect();
                mutationObserver.disconnect();
            };
        },
        unmounted(el) {
            el._scrollObserverCleanup?.();
        },
    });

    if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.init(
            {
                dsn: import.meta.env.VITE_SENTRY_DSN,
                environment: import.meta.env.MODE,
                release: import.meta.env.VITE_SENTRY_RELEASE,
                // iOS hangs are almost all reported with the main thread idle or
                // inside system UI such as context menus and the pasteboard. The
                // app's own code runs in the web view, which hangs can't see.
                enableAppHangTracking: false,
                // Only report errors thrown by our own bundles. Translators and
                // extensions inject scripts that are attributed to the page URL,
                // so match the bundles' path rather than the domain alone.
                allowUrls: import.meta.env.MODE.startsWith('mobile')
                    ? []
                    : [/app\.emedici\.com\/build\//],
                ignoreErrors: [
                    'ResizeObserver loop',
                    'googletag',
                    // Requests cancelled by navigation or by a newer run of the
                    // same request are expected.
                    'AbortError',
                    // Matched whole, allowing the host Sentry appends, so modules
                    // that fail to load after a deploy are still reported.
                    /^(TypeError: )?(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)( \([^)]*\))?$/,
                    // Rejections with no error object, typically from browser
                    // extensions and third-party scripts, have nothing to act on.
                    'captured as promise rejection',
                    'Non-Error promise rejection captured',
                ],
                integrations: [SentryVue.browserTracingIntegration({ router })],
            },
            (options) => SentryVue.init({ ...options, app }),
        );
    }

    watchEffect(() => {
        const { actor } = useGlobalStore();

        Sentry.setUser(
            actor
                ? {
                      id: actor?.id,
                      username: actor?.name || undefined,
                      email: actor?.email || undefined,
                  }
                : null,
        );
    });

    return app;
}

export function hideLoading(): void {
    const loading = document.getElementById('loading');
    if (!loading) return;
    void loading
        .animate({ opacity: [1, 0], transform: ['', 'scale(1.1)'] }, 200)
        .finished.then(() => loading.remove());
}
